import {
  Info,
  CheckCircle2,
  XCircle,
  TrendingUp,
  TrendingDown,
  HelpCircle,
  Lightbulb,
  Award,
  Target,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import { useState } from "react";

const DEFAULT_WEIGHTS = {
  required_skills: 40,
  experience: 30,
  education: 15,
  optional_skills: 15,
};

export default function ScoreExplanation({ matchScore, candidateData, job }) {
  const [open, setOpen] = useState(true);
  
  const score = matchScore?.overall_score || 0;
  const analysis = matchScore?.analysis || {};
  const strengths = analysis.strengths || [];
  const gaps = analysis.gaps || [];
  const skillsAnalysis = analysis.required_skills_analysis || [];

  // Use job's custom weights or defaults
  const jobWeights = job?.scoring_weights || DEFAULT_WEIGHTS;
  const weights = {
    skills: jobWeights.required_skills || 40,
    experience: jobWeights.experience || 30,
    education: jobWeights.education || 15,
    optional: jobWeights.optional_skills || 15,
  };

  const isCustomWeights = job?.scoring_weights && (
    job.scoring_weights.required_skills !== 40 ||
    job.scoring_weights.experience !== 30 ||
    job.scoring_weights.education !== 15 ||
    job.scoring_weights.optional_skills !== 15
  );

  // Calculate actual contributions based on scores
  const contributions = {
    skills: Math.round((matchScore?.required_skills_score || 0) * weights.skills / 100),
    experience: Math.round((matchScore?.experience_score || 0) * weights.experience / 100),
    education: Math.round((matchScore?.education_score || 0) * weights.education / 100),
    optional: Math.round((matchScore?.optional_skills_score || 0) * weights.optional / 100),
  };

  const totalContribution = contributions.skills + contributions.experience + contributions.education + contributions.optional;

  // Generate human-readable explanation
  const getScoreInterpretation = () => {
    if (score >= 85) return "Candidato excepcional que supera la mayoría de los requisitos.";
    if (score >= 70) return "Buen candidato que cumple con los requisitos principales del puesto.";
    if (score >= 50) return "Candidato viable con potencial, cumple requisitos básicos.";
    if (score >= 30) return "Candidato con gaps importantes, requiere evaluación adicional.";
    return "No cumple con los requisitos mínimos del puesto.";
  };

  const getContributionColor = (value, max) => {
    const percentage = (value / max) * 100;
    if (percentage >= 70) return "bg-emerald-500";
    if (percentage >= 40) return "bg-amber-500";
    return "bg-red-400";
  };

  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <CardTitle className="font-heading flex items-center justify-between">
              <div className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-primary" />
                ¿Cómo se calculó este score?
              </div>
              <Badge variant="outline" className="font-normal">
                {open ? "Ocultar" : "Ver detalle"}
              </Badge>
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 space-y-6">
            {/* Main Interpretation */}
            <div className="p-4 rounded-lg bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">{getScoreInterpretation()}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    El score de <strong>{score}%</strong> refleja qué tan bien el perfil del candidato 
                    se alinea con los requisitos de <strong>{job?.title || "este puesto"}</strong>.
                  </p>
                </div>
              </div>
            </div>

            {/* Score Breakdown Visual */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Desglose del Score
                </h4>
                {isCustomWeights && (
                  <Badge variant="secondary" className="text-xs">
                    Pesos personalizados
                  </Badge>
                )}
              </div>
              
              <div className="space-y-3">
                {/* Skills */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-indigo-500"></span>
                      Habilidades Requeridas
                      <Badge variant="outline" className="text-xs">Peso: {weights.skills}%</Badge>
                    </span>
                    <span className="font-medium">
                      +{contributions.skills} puntos
                      <span className="text-muted-foreground ml-1">({matchScore?.required_skills_score || 0}%)</span>
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${getContributionColor(contributions.skills, weights.skills)}`}
                      style={{ width: `${(contributions.skills / weights.skills) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Experience */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                      Experiencia Relevante
                      <Badge variant="outline" className="text-xs">Peso: {weights.experience}%</Badge>
                    </span>
                    <span className="font-medium">
                      +{contributions.experience} puntos
                      <span className="text-muted-foreground ml-1">({matchScore?.experience_score || 0}%)</span>
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${getContributionColor(contributions.experience, weights.experience)}`}
                      style={{ width: `${(contributions.experience / weights.experience) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Education */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                      Educación
                      <Badge variant="outline" className="text-xs">Peso: {weights.education}%</Badge>
                    </span>
                    <span className="font-medium">
                      +{contributions.education} puntos
                      <span className="text-muted-foreground ml-1">({matchScore?.education_score || 0}%)</span>
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${getContributionColor(contributions.education, weights.education)}`}
                      style={{ width: `${(contributions.education / weights.education) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Optional Skills */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-teal-500"></span>
                      Habilidades Deseables
                      <Badge variant="outline" className="text-xs">Peso: {weights.optional}%</Badge>
                    </span>
                    <span className="font-medium">
                      +{contributions.optional} puntos
                      <span className="text-muted-foreground ml-1">({matchScore?.optional_skills_score || 0}%)</span>
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${getContributionColor(contributions.optional, weights.optional)}`}
                      style={{ width: `${(contributions.optional / weights.optional) * 100}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Total */}
              <div className="pt-3 border-t flex justify-between items-center">
                <span className="font-medium">Score Total</span>
                <span className="text-2xl font-bold text-primary">{score}%</span>
              </div>
            </div>

            {/* Skills Analysis Detail */}
            {skillsAnalysis.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Análisis de Habilidades Requeridas
                </h4>
                <div className="grid gap-2">
                  {skillsAnalysis.map((skill, index) => (
                    <div 
                      key={index}
                      className={`p-3 rounded-lg border ${skill.found ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {skill.found ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                          <span className="font-medium">{skill.skill}</span>
                        </div>
                        <Badge variant={skill.found ? "default" : "destructive"} className="text-xs">
                          {skill.score || (skill.found ? 100 : 0)}%
                        </Badge>
                      </div>
                      {skill.evidence && (
                        <p className="text-xs text-muted-foreground mt-1 ml-6">
                          {skill.evidence}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Strengths & Gaps Summary */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Strengths */}
              {strengths.length > 0 && (
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                  <h4 className="font-medium text-emerald-800 flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4" />
                    Fortalezas Detectadas
                  </h4>
                  <ul className="space-y-1.5">
                    {strengths.map((s, i) => (
                      <li key={i} className="text-sm text-emerald-700 flex items-start gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Gaps */}
              {gaps.length > 0 && (
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <h4 className="font-medium text-amber-800 flex items-center gap-2 mb-2">
                    <TrendingDown className="w-4 h-4" />
                    Áreas de Mejora / Brechas
                  </h4>
                  <ul className="space-y-1.5">
                    {gaps.map((g, i) => (
                      <li key={i} className="text-sm text-amber-700 flex items-start gap-2">
                        <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        {g}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Disclaimer */}
            <div className="text-xs text-muted-foreground flex items-start gap-2 pt-2 border-t">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <p>
                Este análisis es generado por IA evaluando la coincidencia entre el CV y los requisitos del puesto. 
                Los pesos reflejan la importancia relativa de cada criterio. Te recomendamos validar con una entrevista.
              </p>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
